import { DEVICE_ID_HEADER } from '@nbh/shared';
import { getDeviceId } from '../lib/device';
import {
  type OutboxEntry,
  getLastSyncAt,
  getPendingWrites,
  markOutboxFailed,
  removeOutboxEntry,
  setLastSyncAt,
} from '../lib/db';

/**
 * Replays queued offline writes (CLAUDE.md Rule 4).
 *
 * The ordering here is the whole point, and it is READ → FETCH → POST → WRITE:
 *
 *   1. read the sync checkpoint,
 *   2. fetch everything still pending,
 *   3. dispatch each write with its **original** timestamps untouched,
 *   4. and only then advance the checkpoint.
 *
 * Advancing the checkpoint first would move this device's sync floor to now. The
 * server clamps `reportedAt` up to that floor (decision D18), so a fault reported at
 * 14:15 in a basement and synced at 14:45 on wifi would be recorded as 14:45 — half
 * an hour of SLA quietly erased, with every request returning 201 as if all was well.
 * That failure is invisible without this order, which is why it is enforced by the
 * shape of the function rather than by a comment.
 */

export interface OutboxPorts {
  getLastSyncAt: () => Promise<Date | null>;
  getPendingWrites: () => Promise<OutboxEntry[]>;
  send: (entry: OutboxEntry) => Promise<void>;
  removeEntry: (id: number) => Promise<void>;
  markFailed: (id: number, error: string) => Promise<void>;
  setLastSyncAt: (at: Date) => Promise<void>;
  now: () => Date;
}

export interface SyncOutcome {
  /** Checkpoint as it was *before* this run — the server's backdating floor. */
  previousSyncAt: Date | null;
  attempted: number;
  sent: number;
  failed: number;
  /** Null when nothing succeeded, so a total failure never advances the checkpoint. */
  checkpointAt: Date | null;
}

/**
 * Sends one queued write, preserving the body exactly as it was captured.
 *
 * The device id is not optional here, and this path is the reason D18 exists. The server
 * clamps `reportedAt` to this device's own last successful sync
 * (`tickets.service.ts:479-486`), it reads that floor from `request.device`, and
 * `DeviceTrackingInterceptor` only populates `request.device` when this header is
 * present. Without it a replayed offline report arrives with no floor at all and its
 * timestamp is taken at face value — so the single path the backdating rule was written
 * to bound was the one path that escaped it.
 *
 * Exported for its test. Everything else here goes through injected ports; this is the
 * real network call, and it is the header that needed pinning.
 */
export async function defaultSend(entry: OutboxEntry): Promise<void> {
  const response = await fetch(`/api${entry.path}`, {
    method: entry.method,
    credentials: 'include',
    headers: { [DEVICE_ID_HEADER]: getDeviceId(), 'Content-Type': 'application/json' },
    // entry.body is forwarded verbatim. Re-stamping any timestamp here would defeat
    // the entire offline queue.
    body: entry.body === undefined ? undefined : JSON.stringify(entry.body),
  });

  if (!response.ok) {
    throw new Error(`Sync failed (${response.status})`);
  }
}

const defaultPorts: OutboxPorts = {
  getLastSyncAt,
  getPendingWrites,
  send: defaultSend,
  removeEntry: removeOutboxEntry,
  markFailed: markOutboxFailed,
  setLastSyncAt,
  now: () => new Date(),
};

// Automatic triggers live in services/networkMonitor, which owns the sole `online`
// listener. Two listeners would each start a drain of the same queue on one event.

export async function syncOutbox(ports: Partial<OutboxPorts> = {}): Promise<SyncOutcome> {
  const p = { ...defaultPorts, ...ports };

  // 1. READ — before anything is written. Captured for the caller so the pre-sync
  //    floor is observable rather than implied.
  const previousSyncAt = await p.getLastSyncAt();

  // 2. FETCH — everything still owed, oldest first.
  const pending = await p.getPendingWrites();

  // 3. POST — one at a time, in the order the work actually happened. A ticket must
  //    exist before its follow-up message, so these are not parallelised.
  let sent = 0;
  let failed = 0;

  for (const entry of pending) {
    try {
      await p.send(entry);
      await p.removeEntry(entry.id);
      sent += 1;
    } catch (error) {
      // One bad entry must not strand the rest of the queue behind it.
      await p.markFailed(entry.id, error instanceof Error ? error.message : String(error));
      failed += 1;
    }
  }

  // 4. WRITE — last, and only on evidence the server was actually reachable. A run
  //    where every send failed leaves the checkpoint untouched, so the floor does not
  //    advance past reports that never made it.
  const checkpointAt = sent > 0 ? p.now() : null;
  if (checkpointAt) await p.setLastSyncAt(checkpointAt);

  return { previousSyncAt, attempted: pending.length, sent, failed, checkpointAt };
}
